import { useCallback, useMemo } from "react";
import moment from "moment";
import currency from "currency.js";
import { useTable, useSortBy } from "react-table";
import { ILiveTrade } from "../../interfaces/liveTrade";
import { shortenAddress } from "../../utils/strings";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { TableWrapper, TableLoader, TableHeaderCell, CopyButton } from "../Table/style";
import { COLORS } from "../../constants/design/colors";
import { useToast, ToastEnum, ToastStatusEnum } from "../../hooks/toast";

interface ILiveTradesTableProps {
  trades: ILiveTrade[];
  isLoading: boolean;
  addressLabels: Record<string, string>;
}

type ITableTradeRow = ILiveTrade & {
  notionalValue: number;
  category: string;
  timestampMs: number;
  isLiquidation: boolean;
  assetLabel: string;
  orderId?: string;
};

const parseTimestamp = (timestamp?: string): number => {
  if (!timestamp) {
    return Date.now();
  }

  const numericTimestamp = Number(timestamp);
  if (!Number.isNaN(numericTimestamp) && numericTimestamp > 0) {
    if (timestamp.length >= 18) {
      return Math.floor(numericTimestamp / 1e6);
    }

    if (timestamp.length === 16 || timestamp.length === 15) {
      return Math.floor(numericTimestamp / 1e3);
    }

    if (timestamp.length === 13) {
      return numericTimestamp;
    }

    if (timestamp.length === 10) {
      return numericTimestamp * 1000;
    }
  }

  const parsedDate = new Date(timestamp);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.getTime();
  }

  return Date.now();
};

const computeNotional = (trade: ILiveTrade) => {
  if (trade.notional !== undefined) {
    const notionalValue = Number(trade.notional);
    if (!Number.isNaN(notionalValue)) {
      return notionalValue;
    }
  }

  const amount = Number(trade.amount);
  const price = Number(trade.avg_price);

  if (Number.isNaN(amount) || Number.isNaN(price)) {
    return 0;
  }

  return amount * price;
};

const determineCategoryLabel = (trade: ILiveTrade) =>
  trade.instrument_type === "OPTION" ? "OPTIONS" : "PERPETUALS";

const deriveAsset = (trade: ILiveTrade) => {
  if (trade.asset) {
    return trade.asset.toUpperCase();
  }

  if (trade.instrument_name) {
    const [asset] = trade.instrument_name.split("-");
    if (asset) {
      return asset.toUpperCase();
    }
  }

  return "-";
};

export function LiveTradesTable({
  trades,
  isLoading,
  addressLabels,
}: ILiveTradesTableProps) {
  const { addToast } = useToast();

  const onCopy = useCallback(
    async (address: string) => {
      if (!address) {
        return;
      }

      try {
        await navigator.clipboard.writeText(address);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Address Copied",
            status: ToastStatusEnum.SUCCESS,
          },
          2000
        );
      } catch (error) {
        // Ignore clipboard errors silently
      }
    },
    [addToast]
  );

  const getAddressLabel = useCallback(
    (address?: string) => {
      if (!address) {
        return undefined;
      }
      const normalized = address.toLowerCase();
      return addressLabels[normalized] ?? addressLabels[address];
    },
    [addressLabels]
  );

  const getAddressDisplay = useCallback(
    (address: string) => {
      const label = getAddressLabel(address);
      const shortAddress = shortenAddress(address);
      const displayText = label ? `${label} • ${shortAddress}` : shortAddress;
      const title = label ? `${label} (${address})` : address;
      return { displayText, title };
    },
    [getAddressLabel]
  );

  const rows = useMemo<ITableTradeRow[]>(() => {
    if (!trades) {
      return [];
    }

    return trades.map((trade) => ({
      ...trade,
      notionalValue: computeNotional(trade),
      category: determineCategoryLabel(trade),
      timestampMs: parseTimestamp(trade.timestamp ?? trade.created_timestamp),
      isLiquidation: Boolean(trade.liquidation || trade.is_liquidation),
      assetLabel: deriveAsset(trade),
      orderId:
        trade.taker_order_id ??
        trade.agg_order_id ??
        trade.aggressing_order_id ??
        undefined,
    }));
  }, [trades]);

  const hasOptions = useMemo(
    () => rows.some((row) => row.instrument_type === "OPTION"),
    [rows]
  );

  const columns = useMemo<ITableColumn<ITableTradeRow>[]>(
    () => {
      const baseColumns: ITableColumn<ITableTradeRow>[] = [];

      baseColumns.push(
        {
          title: "Time",
          accessor: "timestampMs",
          valueExtractor: (value) =>
            moment(value).format("DD MMM YYYY HH:mm:ss"),
        },
        {
          title: "Category",
          accessor: "category",
        },
        {
          title: "Asset",
          accessor: "assetLabel",
        },
        {
          title: "Instrument",
          accessor: "instrument_name",
        },
        {
          title: "Side",
          accessor: "side",
          Cell: ({ value }) => (
            <span
              style={{
                color: value === "buy" ? COLORS.green.one : COLORS.red.one,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          ),
        },
        {
          title: "Order Type",
          accessor: "order_type",
          valueExtractor: (value) => (value ? value.toUpperCase() : "-"),
        },
        {
          title: "Liquidation",
          accessor: "isLiquidation",
          valueExtractor: (value: boolean) => (value ? "Yes" : "No"),
        }
      );

      if (hasOptions) {
        baseColumns.push({
          title: "IV",
          accessor: "iv",
          align: "right",
          Cell: ({ value }) => {
            if (!value) {
              return <span>-</span>;
            }

            const numericIv = Number(value);
            if (Number.isNaN(numericIv)) {
              return <span>-</span>;
            }

            const formattedIv = numericIv > 1 ? numericIv : numericIv * 100;
            return <span>{formattedIv.toFixed(2)}%</span>;
          },
        });
      }

      baseColumns.push(
        {
          title: "Price (USD)",
          accessor: "avg_price",
          align: "right",
          valueExtractor: (value) =>
            value
              ? currency(value, { symbol: "$", precision: 2 }).format()
              : "-",
        },
        {
          title: "Amount",
          accessor: "amount",
          align: "right",
          valueExtractor: (value) =>
            value
              ? currency(value, { symbol: "", precision: 4 }).format()
              : "-",
        },
        {
          title: "Notional (USD)",
          accessor: "notionalValue",
          align: "right",
          valueExtractor: (value) =>
            currency(value, { symbol: "$", precision: 2 }).format(),
        },
        {
          title: "Trade ID",
          accessor: "trade_id",
          Cell: ({ value, row }) => {
            const idValue = value ?? row.original.id;
            if (!idValue) {
              return <span>-</span>;
            }

            return (
              <CopyButton
                type="button"
                title="Copy trade id"
                onClick={(event) => {
                  event.stopPropagation();
                  onCopy(idValue);
                }}
              >
                {shortenAddress(idValue)}
              </CopyButton>
            );
          },
        },
        {
          title: "Order ID",
          accessor: "orderId",
          Cell: ({ value }) => {
            if (!value) {
              return <span>-</span>;
            }

            return (
              <CopyButton
                type="button"
                title="Copy order id"
                onClick={(event) => {
                  event.stopPropagation();
                  onCopy(value);
                }}
              >
                {shortenAddress(value)}
              </CopyButton>
            );
          },
        },
        {
          title: "Taker",
          accessor: "taker",
          Cell: ({ value }) => {
            if (!value) {
              return <span>-</span>;
            }

            const { displayText, title } = getAddressDisplay(value);

            return (
              <CopyButton
                type="button"
                title={title}
                onClick={(event) => {
                  event.stopPropagation();
                  onCopy(value);
                }}
              >
                {displayText}
              </CopyButton>
            );
          },
        },
        {
          title: "Makers",
          accessor: "maker",
          Cell: ({ value }) => {
            if (!value || !value.length) {
              return <span>-</span>;
            }

            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {value.map((address: string, index: number) => {
                  const { displayText, title } = getAddressDisplay(address);
                  return (
                    <CopyButton
                      key={`${address}-${index}`}
                      type="button"
                      title={title}
                      onClick={(event) => {
                        event.stopPropagation();
                        onCopy(address);
                      }}
                    >
                      {displayText}
                    </CopyButton>
                  );
                })}
              </div>
            );
          },
        }
      );

      return baseColumns;
    },
    [getAddressDisplay, hasOptions, onCopy]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: rows,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          {
            id: "timestampMs",
            desc: true,
          },
        ],
      },
    },
    useSortBy
  );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {tableRows.map((row) => {
            prepareRow(row);
            const { ...rowProps } = row.getRowProps();
            return (
              <tr {...rowProps}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
          {!isLoading && tableRows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No trades match the selected filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableWrapper>
  );
}
